import React, { useEffect, useRef, useState } from "react";
import ReactFlagsSelect from "react-flags-select";
import {
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiPlus,
} from "react-icons/fi";
import FormModal from "../../FormModal";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  fetchKybOnboardingOptions,
  submitKybBasicInfo,
  submitKybAddress,
  submitKybDocument,
  submitKybDirector,
  updateKybDirector,
  updateKybDirectorAddress,
  markDirectorAsPrimaryOwner,
  sendDirectorKycRequest,
  deleteKybDirector,
  fetchKybSummary,
  submitKybRequest,
  deleteKybDocument,
} from "../../../actions/user";
import { formatLabel } from "../../../actions/payments";
import { handleValidationError } from "../../../actions/validation";
import { formatDate } from "../../../helpers/date";

const businessSteps = [
  "Basic Business Info",
  "Current Business Address",
  "Business Document Upload",
  "Business Directors",
  "Review and Submit",
];

const initialBusinessKyc = {
  countryOfRegistration: "NG",
  businessName: "",
  description: "",
  websiteUrl: "",
  businessRegistrationTypeId: "",
  businessIndustryId: "",
  registrationNumber: "",
  registrationDate: "",
  taxIdNumber: "",
  address1: "",
  city: "",
  postalCode: "",
  stateProvince: "",
  countryIso2Code: "NG",
};

const fallbackDocumentTypes = [];

const fallbackJobTitles = [
  { id: "CEO", label: "CEO" },
  { id: "COO", name: "COO" },
];

const createBusinessDocument = () => ({
  documentType: "",
  file: null,
});

const createDirector = (isOwner = false, defaultCountry = "NG") => ({
  id: null,
  email: "",
  firstName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  phone: "",
  role: isOwner ? "owner" : "",
  jobTitle: "",
  nationality: "",
  kycIdType: "",
  kycStatus: "pending",
  percentageOwned: isOwner ? "5" : "",
  isPrimaryOwner: isOwner,
  dirAddress: "",
  dirCity: "",
  dirState: "",
  dirPostalCode: "",
  dirCountryIso2: defaultCountry,
  isOwner,
});

const fileName = (file) => file?.name || "No file selected";
const documentUploadKey = (docType) => String(docType || "").toLowerCase();

const flagEmoji = (iso2) =>
  iso2
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(c.charCodeAt(0) + 0x1f1e6 - 0x41))
    .join("");

const COUNTRIES = [
  { iso2: "DZ", name: "Algeria" },
  { iso2: "AO", name: "Angola" },
  { iso2: "BJ", name: "Benin" },
  { iso2: "BW", name: "Botswana" },
  { iso2: "BF", name: "Burkina Faso" },
  { iso2: "BI", name: "Burundi" },
  { iso2: "CV", name: "Cabo Verde" },
  { iso2: "CM", name: "Cameroon" },
  { iso2: "CF", name: "Central African Republic" },
  { iso2: "TD", name: "Chad" },
  { iso2: "KM", name: "Comoros" },
  { iso2: "CD", name: "Congo (DRC)" },
  { iso2: "CG", name: "Congo (Republic)" },
  { iso2: "CI", name: "Côte d'Ivoire" },
  { iso2: "DJ", name: "Djibouti" },
  { iso2: "EG", name: "Egypt" },
  { iso2: "GQ", name: "Equatorial Guinea" },
  { iso2: "ER", name: "Eritrea" },
  { iso2: "SZ", name: "Eswatini" },
  { iso2: "ET", name: "Ethiopia" },
  { iso2: "GA", name: "Gabon" },
  { iso2: "GM", name: "Gambia" },
  { iso2: "GH", name: "Ghana" },
  { iso2: "GN", name: "Guinea" },
  { iso2: "GW", name: "Guinea-Bissau" },
  { iso2: "KE", name: "Kenya" },
  { iso2: "LS", name: "Lesotho" },
  { iso2: "LR", name: "Liberia" },
  { iso2: "LY", name: "Libya" },
  { iso2: "MG", name: "Madagascar" },
  { iso2: "MW", name: "Malawi" },
  { iso2: "ML", name: "Mali" },
  { iso2: "MR", name: "Mauritania" },
  { iso2: "MU", name: "Mauritius" },
  { iso2: "MA", name: "Morocco" },
  { iso2: "MZ", name: "Mozambique" },
  { iso2: "NA", name: "Namibia" },
  { iso2: "NE", name: "Niger" },
  { iso2: "NG", name: "Nigeria" },
  { iso2: "RW", name: "Rwanda" },
  { iso2: "ST", name: "São Tomé & Príncipe" },
  { iso2: "SN", name: "Senegal" },
  { iso2: "SC", name: "Seychelles" },
  { iso2: "SL", name: "Sierra Leone" },
  { iso2: "SO", name: "Somalia" },
  { iso2: "ZA", name: "South Africa" },
  { iso2: "SS", name: "South Sudan" },
  { iso2: "SD", name: "Sudan" },
  { iso2: "TZ", name: "Tanzania" },
  { iso2: "TG", name: "Togo" },
  { iso2: "TN", name: "Tunisia" },
  { iso2: "UG", name: "Uganda" },
  { iso2: "ZM", name: "Zambia" },
  { iso2: "ZW", name: "Zimbabwe" },
];

const CountrySelect = ({ value, onChange, className = "form-control", disabled = false }) => {
  const allowedCodes = COUNTRIES.map((c) => c.iso2);
  const customLabels = COUNTRIES.reduce((acc, curr) => {
    acc[curr.iso2] = curr.name;
    return acc;
  }, {});

  return (
    <ReactFlagsSelect
      selected={value}
      onSelect={(code) => onChange(code)}
      countries={allowedCodes}
      customLabels={customLabels}
      disabled={disabled}
      placeholder="Select country"
      searchable={true}
    />
  );
};

const Field = ({ label, children, required = true, additionalLabelText = ''}) => (
  <div className="form-group">
    <label className="d-block">
      {label} {additionalLabelText && <em style={{color: '#828282'}}>
        ({additionalLabelText})
      </em>} {required && <span className="text-danger">*</span>}
    </label>
    {children}
  </div>
);

const BusinessKycStepper = ({ userDetails }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const defaultCountry = (userDetails?.country_code || userDetails?.country || "NG").toUpperCase();

  const [formData, setFormData] = useState({
    ...initialBusinessKyc,
    businessName: userDetails?.business_name || "",
    countryOfRegistration: defaultCountry,
    countryIso2Code: defaultCountry,
  });
  const [directors, setDirectors] = useState([]);
  const [businessDocuments, setBusinessDocuments] = useState([]);
  const [onboardingOptions, setOnboardingOptions] = useState({
    businessRegistrationTypes: [],
    businessIndustries: [],
    documentTypes: fallbackDocumentTypes,
    jobTitles: fallbackJobTitles,
  });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState(false);
  const [saveChangesLoading, setSaveChangesLoading] = useState(false);
  const [editingDirectorIndex, setEditingDirectorIndex] = useState(null);
  const [addressModalIndex, setAddressModalIndex] = useState(null);
  const [addressDraft, setAddressDraft] = useState({
    address: "",
    city: "",
    state: "",
    postalCode: "",
    countryIso2Code: defaultCountry,
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [directorActionLoading, setDirectorActionLoading] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [debugKybData, setDebugKybData] = useState(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [newDocType, setNewDocType] = useState("");
  const [newDocFile, setNewDocFile] = useState(null);
  const [isDirectorModalOpen, setIsDirectorModalOpen] = useState(false);
  const [directorDraft, setDirectorDraft] = useState(createDirector(false, defaultCountry));
  const [deleteDirectorIndex, setDeleteDirectorIndex] = useState(null);
  const [hasExistingBasicInfo, setHasExistingBasicInfo] = useState(false);
  const [hasExistingAddress, setHasExistingAddress] = useState(false);
  const [documentUploadLoading, setDocumentUploadLoading] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState(null);
  const [kybProgress, setKybProgress] = useState(null);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const kybCanBeSubmitted = ['submitted', 'under_review', 'approved'].includes(userDetails?.business?.kyb_status) ? false : true;

  const loadOnboardingOptions = async (countryCode = defaultCountry) => {
    setOptionsLoading(true);
    try {
      const response = await fetchKybOnboardingOptions(
        countryCode.toUpperCase(),
      );
      if (response?.status === "success") {
        const {
          business_registration_types,
          business_industries,
          director_job_titles,
        } = response.data;
        setOnboardingOptions({
          businessRegistrationTypes: business_registration_types || [],
          businessIndustries: business_industries || [],
          documentTypes: fallbackDocumentTypes,
          jobTitles: director_job_titles?.length 
            ? director_job_titles 
            : fallbackJobTitles,
        });
      } else {
        setOnboardingOptions((prev) => ({
          ...prev,
          documentTypes: fallbackDocumentTypes,
          jobTitles: fallbackJobTitles,
        }));
      }
    } catch {
      setOnboardingOptions((prev) => ({
        ...prev,
        documentTypes: fallbackDocumentTypes,
        jobTitles: fallbackJobTitles,
      }));
    } finally {
      setOptionsLoading(false);
    }
  };

  // console.log('user details', userDetails);
  // console.log('form data', formData);

  useEffect(() => {
    const initData = async () => {
      setInitialLoading(true);
      let country = defaultCountry;
      try {
        const response = await fetchKybSummary();
        // console.log("KYB Summary Response:", response);
        setDebugKybData(response);
        
        const biz = response?.data?.business || response?.business || response?.data;
        
        if (biz && Object.keys(biz).length > 0) {
          const reqDocs = biz.metadata?.required_documents || response?.data?.metadata?.required_documents || response?.metadata?.required_documents;
          if (reqDocs) setRequiredDocuments(reqDocs);
          setKybProgress(biz?.kyb_completion_progress);

          country = biz.country_of_registration || "NG";
          country = biz.country_of_registration || defaultCountry;
          
          const addressSource = biz.operational_address; // business_addresses?.[0] || biz.business_address || biz.address;
          let addr = "";
          let city = "";
          let state = "";
          let postalCode = "";
          let countryIso2 = defaultCountry;

          if (addressSource && typeof addressSource === "object") {
            addr = addressSource.address || addressSource.address1 || addressSource.street || "";
            city = addressSource.city || "";
            state = addressSource.state || addressSource.stateProvince || addressSource.state_province || "";
            postalCode = addressSource.postal_code || addressSource.postalCode || "";
            countryIso2 = addressSource.country_iso2_code || addressSource.country_code || addressSource.countryIso2Code || defaultCountry;
          } else if (biz) {
            addr = biz.address || biz.address1 || biz.street || "";
            city = biz.city || "";
            state = biz.state || biz.stateProvince || biz.state_province || "";
            postalCode = biz.postal_code || biz.postalCode || "";
            countryIso2 = biz.country_iso2_code || biz.country_code || biz.countryIso2Code || defaultCountry;
          }

          setFormData({
            countryOfRegistration: country,
            businessName: biz.name || biz.business_name || userDetails?.business_name || "",
            description: biz.description || "",
            websiteUrl: biz.website_url || "",
            businessRegistrationTypeId: biz.business_registration_type_id || "",
            businessIndustryId: biz.business_industry_id || "",
            registrationNumber: biz.registration_number || "",
            registrationDate: biz.registration_date || "",
            taxIdNumber: biz.tin_number || "",
            address1: addr,
            city: city,
            stateProvince: state,
            postalCode: postalCode,
            countryIso2Code: countryIso2,
          });

          // Map directors
          const savedDirectors = biz.business_directors || biz.directors;
          if (savedDirectors && savedDirectors.length > 0) {
            const mappedDirectors = savedDirectors.map((d) => ({
              id: d.id,
              email: d.email || "",
              firstName: d.first_name || "",
              lastName: d.last_name || "",
              gender: d.gender || "",
              dateOfBirth: d.date_of_birth || "",
              phone: d.phone || "",
              role: d.role || "",
              jobTitle: d.job_title || "",
              nationality: d.nationality || "",
              kycIdType: d.kyc_id_type || "",
              percentageOwned: d.ownership_percentage || "",
              isPrimaryOwner: Boolean(d.is_primary_owner),
              dirAddress: d.address || "",
              dirCity: d.city || "",
              dirPostalCode: d.postal_code || "",
              dirState: d.state || "",
              dirCountryIso2: d.country_iso2_code || "NG",
              isOwner: d.role === "owner" || !!d.is_owner || !!d.is_primary_owner,
              kycStatus: d.kyc_status || 'pending'
            }));
            setDirectors(mappedDirectors);
            setEditingDirectorIndex(null); // default to viewing if we have saved directors
          }

          // Map documents
          const docs = biz.business_documents || biz.documents;
          if (docs && docs.length > 0) {
            const mappedDocs = docs.map((doc) => ({
              id: doc.id,
              documentType: doc.document_type || "",
              file: doc.file_path || doc.file_url 
                ? { name: (doc.file_path || doc.file_url).substring((doc.file_path || doc.file_url).lastIndexOf('/') + 1) } 
                : null,
              filePath: doc.file_path || doc.file_url || null,
              verificationStatus: doc.verification_status || "pending",
              isUploaded: true
            }));
            setBusinessDocuments(mappedDocs);
          }

          // Determine current step based on filled fields
          let step = 0;
          const hasBasicInfo = biz.country_of_registration && (biz.name || biz.business_name) && biz.business_email && biz.registration_number && biz.business_registration_type_id && biz.business_industry_id;
          const hasAddress = addr && city && state && countryIso2;
          const hasDocs = docs && docs.length > 0 && docs.every(doc => doc.document_type && (doc.file_path || doc.file_url));
          const hasDirectors = savedDirectors && savedDirectors.length > 0 && savedDirectors.every(d => d.email && d.first_name && d.last_name);

          setHasExistingBasicInfo(Boolean(hasBasicInfo));
          setHasExistingAddress(Boolean(hasAddress));

          if (hasBasicInfo) {
            step = 1;
            if (hasAddress) {
              step = 2;
              if (hasDocs) {
                step = 3;
                if (hasDirectors) {
                  step = 4;
                }
              }
            }
          }
          setCurrentStep(step);
        }
      } catch (err) { 
        setDebugKybData({ error: err.message, response: err.response?.data });
      } finally {
        setInitialLoading(false);
        loadOnboardingOptions(country);
      }
    };

    initData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateDirector = (index, field, value) => {
    setDirectors((prev) =>
      prev.map((director, directorIndex) => {
        if (directorIndex !== index) return director;
        return { ...director, [field]: value };
      }),
    );
  };

  const updateBusinessDocument = (index, field, value) => {
    setBusinessDocuments((prev) =>
      prev.map((document, documentIndex) =>
        documentIndex === index ? { ...document, [field]: value } : document,
      ),
    );
  };

  const removeBusinessDocument = async (index) => {
    const document = businessDocuments[index];
    setBusinessDocuments((prev) =>
      prev.filter((_, documentIndex) => documentIndex !== index),
    );
    try {
      const response = await deleteKybDocument(document.id);
      setKybProgress(response?.data?.business?.kyb_completion_progress);
      toast.success(response?.message || "Document removed successfully.");
    } catch (err) {
      handleStepApiError(err);
    } finally {
      
    }
  };

  const handleSaveNewDocument = async () => {
    if (!newDocType || !newDocFile) {
      toast.error("Please select a document type and select a file.");
      return;
    }
    const uploadKey = documentUploadKey(newDocType);
    setDocumentUploadLoading((prev) => ({ ...prev, [uploadKey]: true }));
    try {
      const fd = new FormData();
      fd.append("document_type", newDocType);
      fd.append("uploaded_file", newDocFile);
      const response = await submitKybDocument(fd);
      
      const savedDocId = response?.data?.document?.id || response?.document?.id || null;
      const savedPath = response?.data?.file_path || response?.file_path || null;
      setKybProgress(response?.data?.business?.kyb_completion_progress);

      setBusinessDocuments((prev) => [
        ...prev,
        {
          id: savedDocId,
          documentType: newDocType,
          file: newDocFile,
          filePath: savedPath,
          isUploaded: true,
          verificationStatus: "pending",
        },
      ]);
      setNewDocType("");
      setNewDocFile(null);
      setIsDocumentModalOpen(false);
      toast.success("Document uploaded successfully.");
    } catch (err) {
      handleStepApiError(err);
    } finally {
      setDocumentUploadLoading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleDirectUpload = async (docType, file) => {
    if (!docType || !file) return;
    const uploadKey = documentUploadKey(docType);
    setDocumentUploadLoading((prev) => ({ ...prev, [uploadKey]: true }));
    try {
      const fd = new FormData();
      fd.append("document_type", docType);
      fd.append("uploaded_file", file);
      const response = await submitKybDocument(fd);
      
      const savedDocId = response?.data?.document?.id || response?.document?.id || null;
      const savedPath = response?.data?.file_path || response?.file_path || null;
      setKybProgress(response?.data?.business?.kyb_completion_progress);

      setBusinessDocuments((prev) => {
        const existingIndex = prev.findIndex(d => 
          String(d.documentType).toLowerCase() === String(docType).toLowerCase()
        );
        const newDoc = {
          id: savedDocId,
          documentType: docType,
          file: file,
          filePath: savedPath,
          isUploaded: true,
          verificationStatus: "pending",
        };
        if (existingIndex >= 0) {
           const updated = [...prev];
           updated[existingIndex] = newDoc;
           return updated;
        }
        return [...prev, newDoc];
      });
      toast.success("Document uploaded successfully.");
    } catch (err) {
      handleStepApiError(err);
    } finally {
      setDocumentUploadLoading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };


  const addDirector = () => {
    setDirectors((prev) => [...prev, createDirector(false, defaultCountry)]);
    setEditingDirectorIndex(directors.length);
  };

  const openAddressModal = (index) => {
    setAddressModalIndex(index);
    const d = directors[index];
    setAddressDraft({
      address: d?.dirAddress || "",
      city: d?.dirCity || "",
      state: d?.dirState || "",
      postalCode: d?.dirPostalCode || "",
      countryIso2Code: d?.dirCountryIso2 || defaultCountry,
    });
  };

  const saveAddress = async () => {
    if (addressModalIndex === null) return;
    const director = directors[addressModalIndex];
    setAddressLoading(true);
    try {
      if (director?.id) {
        const response = await updateKybDirectorAddress(director.id, {
          address: addressDraft.address,
          city: addressDraft.city,
          state: addressDraft.state,
          postal_code: addressDraft.postalCode,
          country_iso2_code: addressDraft.countryIso2Code,
        });
        toast.success("Director address updated successfully.");
        setKybProgress(response?.data?.business?.kyb_completion_progress);
      }
      setDirectors((prev) =>
        prev.map((d, i) =>
          i === addressModalIndex
            ? {
                ...d,
                dirAddress: addressDraft.address,
                dirCity: addressDraft.city,
                dirPostalCode: addressDraft.postalCode,
                dirState: addressDraft.state,
                dirCountryIso2: addressDraft.countryIso2Code,
              }
            : d,
        ),
      );
      setAddressModalIndex(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to update address.";
      toast.error(msg);
    } finally {
      setAddressLoading(false);
    }
  };

  const openAddDirectorModal = () => {
    setDirectorDraft(createDirector(directors.length === 0, defaultCountry));
    setEditingDirectorIndex(null);
    setIsDirectorModalOpen(true);
  };

  const openEditDirectorModal = (index) => {
    setDirectorDraft({ ...directors[index] });
    setEditingDirectorIndex(index);
    setIsDirectorModalOpen(true);
  };

  const handleUpdateDirectorDraft = (field, value) => {
    setDirectorDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDirectorModal = async () => {
    // Validate required fields locally
    const requiredDirectorFields = [
      "email",
      "firstName",
      "lastName",
      "gender",
      "dateOfBirth",
      "phone",
      "role",
      "jobTitle",
      "nationality",
      "percentageOwned",
    ];
    // console.log(directorDraft);
    const missing = requiredDirectorFields.filter((field) => !directorDraft[field]);
    if (missing.length > 0) {
      const displayNames = missing.map((f) => f.replace(/([A-Z])/g, " $1").toLowerCase());
      toast.error(`Please complete missing fields: ${displayNames.join(", ")}`);
      return;
    }

    const value = Number(directorDraft.percentageOwned);
    if (Number.isNaN(value) || value < 0 || value > 100 || (directorDraft.isOwner && value < 5)) {
      toast.error("Director ownership must be between 0% and 100%. The business owner must own at least 5%.");
      return;
    }

    setStepLoading(true);
    try {
      if (editingDirectorIndex !== null) {
        const director = directors[editingDirectorIndex];
        if (director.id) {
          const response = await updateKybDirector(director.id, {
            email: directorDraft.email,
            first_name: directorDraft.firstName,
            last_name: directorDraft.lastName,
            gender: directorDraft.gender,
            date_of_birth: directorDraft.dateOfBirth,
            phone: directorDraft.phone,
            role: directorDraft.role,
            job_title: directorDraft.jobTitle,
            nationality: directorDraft.nationality,
            ownership_percentage: directorDraft.percentageOwned,
            kyc_id_type: directorDraft.kycIdType,
            is_primary_owner: directorDraft.isPrimaryOwner,
          });
          toast.success("Director updated successfully.");
          setKybProgress(response?.data?.business?.kyb_completion_progress);
          setDirectors((prev) =>
            prev.map((d, i) => (i === editingDirectorIndex ? { ...directorDraft } : d))
          );
        } else {
          const response = await submitKybDirector({
            email: directorDraft.email,
            first_name: directorDraft.firstName,
            last_name: directorDraft.lastName,
            gender: directorDraft.gender,
            date_of_birth: directorDraft.dateOfBirth,
            phone: directorDraft.phone,
            role: directorDraft.role,
            job_title: directorDraft.jobTitle,
            nationality: directorDraft.nationality,
            kyc_id_type: directorDraft.kycIdType,
            ownership_percentage: directorDraft.percentageOwned,
            is_primary_owner: directorDraft.isPrimaryOwner,
          });
          const savedId =
            response?.data?.director?.id ||
            response?.data?.business?.business_directors?.slice(-1)[0]?.id ||
            null;
          toast.success("Director saved successfully.");
          setKybProgress(response?.data?.business?.kyb_completion_progress);
          setDirectors((prev) =>
            prev.map((d, i) => (i === editingDirectorIndex ? { ...directorDraft, id: savedId } : d))
          );
        }
      } else {
        const response = await submitKybDirector({
          email: directorDraft.email,
          first_name: directorDraft.firstName,
          last_name: directorDraft.lastName,
          gender: directorDraft.gender,
          date_of_birth: directorDraft.dateOfBirth,
          phone: directorDraft.phone,
          role: directorDraft.role,
          job_title: directorDraft.jobTitle,
          nationality: directorDraft.nationality,
          kyc_id_type: directorDraft.kycIdType,
          ownership_percentage: directorDraft.percentageOwned,
          is_primary_owner: directorDraft.isPrimaryOwner,
        });
        const savedId =
          response?.data?.director?.id ||
          response?.data?.business?.business_directors?.slice(-1)[0]?.id ||
          null;
        toast.success("Director added successfully.");
        setKybProgress(response?.data?.business?.kyb_completion_progress);
        setDirectors((prev) => [...prev, { ...directorDraft, id: savedId }]);
      }
      setIsDirectorModalOpen(false);
      setEditingDirectorIndex(null);
    } catch (err) {
      handleStepApiError(err);
    } finally {
      setStepLoading(false);
    }
  };

  const handleMarkAsPrimary = async (index) => {
    const director = directors[index];
    if (!director?.id) return;
    const loadKey = `primary-${index}`;
    setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: true }));
    try {
      await markDirectorAsPrimaryOwner(director.id);
      toast.success(
        `${director.firstName} ${director.lastName} is now the primary owner.`,
      );
      setDirectors((prev) =>
        prev.map((d, i) => ({ ...d, isPrimaryOwner: i === index })),
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to mark as primary owner.",
      );
    } finally {
      setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: false }));
    }
  };

  const handleSendKycInvite = async (index) => {
    const director = directors[index];
    if (!director?.id) return;
    const loadKey = `kyc-${index}`;
    setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: true }));
    try {
      const response = await sendDirectorKycRequest(director.id);
      const expiresStr = response?.data?.expires ? ` (Expires: ${response.data.expires})` : "";
      const successMsg = response?.message || `KYC invite sent successfully to ${director.firstName || "director"}.${expiresStr}`;
      toast.success(successMsg);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send KYC invite.");
    } finally {
      setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: false }));
    }
  };

  const handleDeleteDirector = (index) => {
    if (directors.length === 1) {
      toast.error("You must have at least one director.");
      return;
    }

    const director = directors[index];

    // If the director doesn't have an ID (not saved to backend yet), just remove from state
    if (!director?.id) {
      setDirectors((prev) => prev.filter((_, i) => i !== index));
      if (editingDirectorIndex === index) {
        setEditingDirectorIndex(null);
      } else if (editingDirectorIndex > index) {
        setEditingDirectorIndex((prev) => prev - 1);
      }
      toast.success("Director removed successfully.");
      return;
    }

    setDeleteDirectorIndex(index);
  };

  const confirmDeleteDirector = async () => {
    if (deleteDirectorIndex === null) return;

    const index = deleteDirectorIndex;
    const director = directors[index];
    if (!director?.id) {
      setDeleteDirectorIndex(null);
      return;
    }

    const loadKey = `delete-${index}`;
    setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: true }));
    try {
      const response = await deleteKybDirector(director.id);
      toast.success("Director deleted successfully.");
      setKybProgress(response?.data?.business?.kyb_completion_progress);
      setDeleteDirectorIndex(null);
      setDirectors((prev) => prev.filter((_, i) => i !== index));
      if (editingDirectorIndex === index) {
        setEditingDirectorIndex(null);
      } else if (editingDirectorIndex > index) {
        setEditingDirectorIndex((prev) => prev - 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete director.");
    } finally {
      setDirectorActionLoading((prev) => ({ ...prev, [loadKey]: false }));
    }
  };

  const validateStep = () => {
    const requiredByStep = [
      [
        "countryOfRegistration",
        "businessName",
        "description",
        "registrationNumber",
        "registrationDate",
        "businessRegistrationTypeId",
        "businessIndustryId",
      ],
      ["address1", "city", "stateProvince", "countryIso2Code"],
      [],
      [],
      [],
    ];

    const missingField = requiredByStep[currentStep].some(
      (field) => !formData[field],
    );
    if (missingField) {
      toast.error("Please complete all required fields before continuing.");
      return false;
    }

    if (currentStep === 2) {
      const hasIncompleteDocument = businessDocuments.some(
        (doc) => !doc.documentType || !doc.file,
      );
      if (hasIncompleteDocument) {
        toast.error(
          "Please select a document type and upload a file for each document.",
        );
        return false;
      }
    }

    if (currentStep === 3) {
      const requiredDirectorFields = [
        "email",
        "firstName",
        "lastName",
        "gender",
        "dateOfBirth",
        "phone",
        "role",
        "jobTitle",
        "nationality",
        "percentageOwned",
      ];

      for (let i = 0; i < directors.length; i++) {
        const director = directors[i];
        const missing = requiredDirectorFields.filter(
          (field) => !director[field],
        );
        if (missing.length > 0) {
          const displayNames = missing.map((f) =>
            f.replace(/([A-Z])/g, " $1").toLowerCase(),
          );
          toast.error(
            `Please complete missing fields for Director ${
              i + 1
            }: ${displayNames.join(", ")}`,
          );
          return false;
        }
      }
      const invalidPercentage = directors.some((director) => {
        const value = Number(director.percentageOwned);
        return (
          Number.isNaN(value) ||
          value < 0 ||
          value > 100 ||
          (director.isOwner && value < 5)
        );
      });
      if (invalidPercentage) {
        toast.error(
          "Director ownership must be between 0% and 100%. The business owner must own at least 5%.",
        );
        return false;
      }
    }

    return true;
  };

  const handleStepApiError = (err) => {
    const errors = err?.response?.data?.errors;
    const msg =
      err?.response?.data?.message || "An error occurred. Please try again.";
    if (Array.isArray(errors)) {
      const firstError = errors[0]?.message || msg;
      toast.error(firstError);
    } else if (errors) {
      const firstError = Object.values(errors).flat()[0];
      toast.error(firstError || msg);
    } else {
      toast.error(msg);
    }
  };

  const handleSaveStepInfo = async () => {
    if (!validateStep()) return;
    setSaveChangesLoading(true);
    try {
      if (currentStep === 0) {
        const payload = new FormData();
        payload.append("name", formData.businessName);
        payload.append("registration_number", formData.registrationNumber);
        payload.append("registration_date", formData.registrationDate);
        payload.append("tin_number", formData.taxIdNumber);
        payload.append("description", formData.description);
        payload.append(
          "business_registration_type_id",
          formData.businessRegistrationTypeId,
        );
        payload.append("business_industry_id", formData.businessIndustryId);
        payload.append(
          "country_of_registration",
          formData.countryOfRegistration,
        );
        if (formData.websiteUrl) {
          payload.append("website_url", formData.websiteUrl);
        }
        const response = await submitKybBasicInfo(payload);
        const reqDocs = response?.data?.business?.metadata?.required_documents || response?.data?.metadata?.required_documents || response?.metadata?.required_documents;
        if (reqDocs) setRequiredDocuments(reqDocs);
        setKybProgress(response?.data?.business?.kyb_completion_progress);
        setHasExistingBasicInfo(true);
        toast.success("Business info saved successfully.");
      } else if (currentStep === 1) {
        const response = await submitKybAddress({
          address: formData.address1,
          city: formData.city,
          state: formData.stateProvince,
          postal_code: formData.postalCode,
          country_iso2_code: formData.countryIso2Code,
        });
        const reqDocs = response?.data?.business?.metadata?.required_documents || response?.data?.metadata?.required_documents || response?.metadata?.required_documents;
        if (reqDocs) setRequiredDocuments(reqDocs);
        setKybProgress(response?.data?.business?.kyb_completion_progress);
        setHasExistingAddress(true);
        toast.success("Business address saved successfully.");
      }
    } catch (err) {
      handleStepApiError(err);
    } finally {
      setSaveChangesLoading(false);
    }
  };

  const handleSaveAndContinue = async (event) => {
    event?.preventDefault();
    if (!validateStep()) return;
    setStepLoading(true);
    try {
      if (currentStep === 0) {
        const payload = new FormData();
        payload.append("name", formData.businessName);
        payload.append("registration_number", formData.registrationNumber);
        payload.append("registration_date", formData.registrationDate);
        payload.append("tin_number", formData.taxIdNumber);
        payload.append("description", formData.description);
        payload.append(
          "business_registration_type_id",
          formData.businessRegistrationTypeId,
        );
        payload.append("business_industry_id", formData.businessIndustryId);
        payload.append(
          "country_of_registration",
          formData.countryOfRegistration,
        );
        if (formData.websiteUrl)
          payload.append("website_url", formData.websiteUrl);
        const response = await submitKybBasicInfo(payload);
        const reqDocs = response?.data?.business?.metadata?.required_documents || response?.data?.metadata?.required_documents || response?.metadata?.required_documents;
        if (reqDocs) setRequiredDocuments(reqDocs);
        setKybProgress(response?.data?.business?.kyb_completion_progress);
        setHasExistingBasicInfo(true);
        toast.success("Business info saved successfully.");
      } else if (currentStep === 1) {
        const response = await submitKybAddress({
          address: formData.address1,
          city: formData.city,
          state: formData.stateProvince,
          postal_code: formData.postalCode,
          country_iso2_code: formData.countryIso2Code,
        });
        const reqDocs = response?.data?.business?.metadata?.required_documents || response?.data?.metadata?.required_documents || response?.metadata?.required_documents;
        if (reqDocs) setRequiredDocuments(reqDocs);
        setKybProgress(response?.data?.business?.kyb_completion_progress);
        setHasExistingAddress(true);
        toast.success("Business address saved successfully.");
      }
      setCurrentStep((step) => Math.min(step + 1, businessSteps.length - 1));
    } catch (err) {
      handleStepApiError(err);
    } finally {
      setStepLoading(false);
    }
  };

  const goNext = (event) => {
    event?.preventDefault();
    if (!validateStep()) return;
    setCurrentStep((step) => Math.min(step + 1, businessSteps.length - 1));
  };

  const goBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const submitBusinessKyc = async (event) => {
    event.preventDefault();

    if (currentStep < businessSteps.length - 1) {
      return;
    }

    setSubmitErrors([]);
    if (!validateStep()) return;

    // if (getStepProgress(4) < 100) {
    //   toast.error("Please ensure all steps are 100% complete before submitting.");
    //   return;
    // }

    if (!declarationAccepted) {
      toast.error("Please read and accept the declaration before submitting.");
      return;
    }

    setStepLoading(true);
    try {
      await submitKybRequest();

      toast.success("Business KYB submitted successfully for review.");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      const errors = err?.response?.data?.errors;
      setSubmitErrors(Array.isArray(errors) ? errors : []);
      handleStepApiError(err);
    } finally {
      setStepLoading(false);
    }
  };

  const mappedRequiredDocuments = requiredDocuments?.length && onboardingOptions.documentTypes.length
    ? requiredDocuments.map((req) => {
        const match = onboardingOptions.documentTypes.find(
          (opt) => {
            const optCode = opt.code || opt.id || "";
            const optLabel = opt.name || opt.label || opt.value || "";
            return optCode.toLowerCase() === req.code?.toLowerCase() ||
                   optLabel.toLowerCase() === req.label?.toLowerCase() ||
                   optLabel.toLowerCase() === req.code?.toLowerCase() ||
                   optCode.toLowerCase() === req.label?.toLowerCase();
          }
        );
        return match ? { ...req, id: match.id || match.code || match.value } : req;
      })
    : requiredDocuments;

  const activeDocumentTypes = mappedRequiredDocuments?.length
    ? mappedRequiredDocuments
    : onboardingOptions.documentTypes.length
    ? onboardingOptions.documentTypes
    : fallbackDocumentTypes;

  const getDocumentTypeValue = (doc) =>
    doc && typeof doc === "object"
      ? doc.id || doc.code || doc.value || doc.name || ""
      : doc || "";

  const getDocumentTypeLabel = (doc) =>
    doc && typeof doc === "object"
      ? doc.name || doc.label || doc.value || doc.code || ""
      : doc || "";

  const getBusinessDocumentTitle = (document) => {
    const documentType = activeDocumentTypes.find((type) => {
      const typeValue = String(getDocumentTypeValue(type)).toLowerCase();
      const typeLabel = String(getDocumentTypeLabel(type)).toLowerCase();
      const savedType = String(document.documentType || "").toLowerCase();
      return savedType && (typeValue === savedType || typeLabel === savedType);
    });

    return getDocumentTypeLabel(documentType) || formatLabel(document.documentType) || "Document type pending";
  };

  const selectedDocumentType = activeDocumentTypes.find(
    (doc) =>
      String(getDocumentTypeValue(doc)).toLowerCase() ===
      String(newDocType).toLowerCase(),
  );

  const selectedDocumentDescription =
    selectedDocumentType && typeof selectedDocumentType === "object"
      ? selectedDocumentType.description || ""
      : "";

  const declarantName = (userDetails?.first_name ? `${userDetails?.first_name} ${userDetails?.last_name}` : userDetails?.name)  || "the authorised representative";
  const declarationBusinessName = formData.businessName || "the Business/Merchant";

  const activeDirectorJobTitles = onboardingOptions.jobTitles.length
    ? onboardingOptions.jobTitles
    : fallbackJobTitles;

  const getStepProgress = (index) => {
    return kybProgress[`step_${index + 1}`];
  };

  if (initialLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const directorPendingDelete =
    deleteDirectorIndex !== null ? directors[deleteDirectorIndex] : null;
  const directorPendingDeleteName = [
    directorPendingDelete?.firstName,
    directorPendingDelete?.lastName,
  ]
    .filter(Boolean)
    .join(" ");
  const isDeletingDirector =
    deleteDirectorIndex !== null &&
    !!directorActionLoading[`delete-${deleteDirectorIndex}`];

  return (
    <div className="business-kyc">
      <div className="business-kyc__steps mb-4">
        {businessSteps.map((step, index) => {
          const percentageCompletion = getStepProgress(index);
          return (
            <button
              type="button"
              key={step}
              className={`business-kyc__step ${
                index === currentStep ? "active" : ""
              } ${index < currentStep ? "done" : ""}`}
              onClick={() => {
                if (index < currentStep || validateStep()) {
                  setCurrentStep(index);
                }
              }}
            >
              <span>{index < currentStep ? <FiCheck /> : index + 1}</span>
              
              <div className="d-flex flex-column align-items-center" style={{ lineHeight: '1.2' }}>
                <small>{step}</small>
                {index != 4 && <small style={{ 
                  fontSize: "0.65rem", 
                  color: percentageCompletion == 100 ? '#28a745' : (percentageCompletion > 0 ? '#fa8e12' : '#7d6c6c'), 
                  marginTop: '2px' 
                }}>
                  {percentageCompletion}% Completed
                </small>}
              </div>
            </button>
          )
        })}
      </div>

      <form onSubmit={submitBusinessKyc}>
       
        {currentStep === 0 && (
          <div className="business-kyc__panel">
            <h6>Basic Business Info</h6>
            <div className="row">
              <div className="col-md-6">
                <Field label="Country of Registration">
                  <CountrySelect
                    value={formData.countryOfRegistration}
                    onChange={(iso2) => {
                      updateFormData("countryOfRegistration", iso2);
                      updateFormData("countryIso2Code", iso2);
                      if (iso2.length === 2) loadOnboardingOptions(iso2);
                    }}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Business Name">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.businessName}
                    onChange={(e) =>
                      updateFormData("businessName", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Business Registration Type">
                  {optionsLoading ? (
                    <select className="form-control" disabled>
                      <option>Loading…</option>
                    </select>
                  ) : (
                    <select
                      className="form-control"
                      value={formData.businessRegistrationTypeId}
                      onChange={(e) =>
                        updateFormData(
                          "businessRegistrationTypeId",
                          e.target.value,
                        )
                      }
                    >
                      <option value="">Select registration type</option>
                      {onboardingOptions.businessRegistrationTypes.map(
                        (type) => (
                          <option value={type.id} key={type.id}>
                            {type.name}
                          </option>
                        ),
                      )}
                    </select>
                  )}
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Business Industry">
                  {optionsLoading ? (
                    <select className="form-control" disabled>
                      <option>Loading…</option>
                    </select>
                  ) : (
                    <select
                      className="form-control"
                      value={formData.businessIndustryId}
                      onChange={(e) =>
                        updateFormData("businessIndustryId", e.target.value)
                      }
                    >
                      <option value="">Select industry</option>
                      {onboardingOptions.businessIndustries.map((industry) => (
                        <option value={industry.id} key={industry.id}>
                          {industry.name}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Registration Number">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.registrationNumber}
                    onChange={(e) =>
                      updateFormData("registrationNumber", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Registration Date">
                  <input
                    type="date"
                    className="form-control"
                    value={formData.registrationDate ? formatDate(formData.registrationDate) : ""}
                    onChange={(e) => updateFormData("registrationDate", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Tax ID Number" required={false} additionalLabelText="Optional">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.taxIdNumber}
                    onChange={(e) =>
                      updateFormData("taxIdNumber", e.target.value)
                    }
                    placeholder="Enter Tax ID number"
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Website URL" required={false} additionalLabelText="Optional">
                  <input
                    type="url"
                    className="form-control"
                    placeholder="https://example.com"
                    value={formData.websiteUrl}
                    onChange={(e) =>
                      updateFormData("websiteUrl", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-12">
                <Field label="Business Description">
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.description}
                    onChange={(e) =>
                      updateFormData("description", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div className="business-kyc__panel">
            <h6>Current Business Address</h6>
            <div className="row">
              <div className="col-12">
                <Field label="Address">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.address1}
                    onChange={(e) => updateFormData("address1", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-3">
                <Field label="City">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.city}
                    onChange={(e) => updateFormData("city", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-3">
                <Field label="State / Province">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.stateProvince}
                    onChange={(e) =>
                      updateFormData("stateProvince", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-md-3">
                <Field label="Postal/Zip Code">
                  <input
                    type="text"
                    className="form-control"
                    value={formData.postalCode}
                    onChange={(e) =>
                      updateFormData("postalCode", e.target.value)
                    }
                    placeholder="e.g. 3002918"
                  />
                </Field>
              </div>
              <div className="col-md-3">
                <Field label="Country">
                  <CountrySelect
                    value={formData.countryIso2Code}
                    onChange={(iso2) => updateFormData("countryIso2Code", iso2)}
                  />
                </Field>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="business-kyc__panel">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
              <div>
                <h5 className="mb-1 fw-bold">Business Registration Documents</h5>
                <p className="text-muted small mb-0">Upload the required documents below to complete your KYB verification process.</p>
              </div>
            </div>

            <div className="business-kyc__documents-list">
              {activeDocumentTypes.map((doc, idx) => {
                const val = getDocumentTypeValue(doc);
                const label = getDocumentTypeLabel(doc);
                const isUploadingDocument = documentUploadLoading ? Boolean(documentUploadLoading[documentUploadKey(val)]) : null;
                const uploadedDoc = businessDocuments.find(d => 
                  String(d.documentType).toLowerCase() === String(val).toLowerCase()
                );
                
                return (
                  <div key={`doc-upload-${idx}`} className="mb-5">
                    <label className="d-block fw-bold mb-3">{label} <span className="text-danger">*</span></label>
                    
                    {uploadedDoc ? (
                      <div className="mt-2">
                        <div className="p-3 border rounded bg-white shadow-sm position-relative overflow-hidden d-flex align-items-center justify-content-between gap-2">
                          <div className="d-flex align-items-center gap-3 overflow-hidden">
                            <div className="bg-white rounded-circle text-muted d-flex align-items-center justify-content-center border shadow-sm flex-shrink-0" style={{width: '50px', height: '50px'}}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                            </div>
                            <div className="mb-1 text-truncate">
                              <div className="fw-bold text-dark text-truncate" style={{fontSize: '0.95rem'}} title={fileName(uploadedDoc.file)}>{fileName(uploadedDoc.file)}</div>
                              {uploadedDoc.file?.size && (
                                <div className="text-muted" style={{fontSize: '0.8rem'}}>
                                  {(uploadedDoc.file.size / 1024).toFixed(0)} KB
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '4px' }}>
                            <div className="bg-success h-100" style={{ width: '100%' }}></div>
                          </div>
                          <button 
                            type="button" 
                            className="btn btn-sm text-danger fw-bold border-0 bg-transparent text-decoration-none flex-shrink-0"
                            style={{ zIndex: 11 }}
                            onClick={(e) => {
                               e.stopPropagation();
                               e.preventDefault();
                               const docIndex = businessDocuments.indexOf(uploadedDoc);
                               if (docIndex >= 0) removeBusinessDocument(docIndex);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="position-relative border rounded p-4 text-start bg-white mb-4" style={{ 
                        cursor: "pointer",  }}>
                        <input
                          type="file"
                          className="position-absolute w-100 h-100 opacity-0 top-0 start-0" 
                          style={{ cursor: "pointer", zIndex: 10 , borderColor: "#c0b9b9ff", borderStyle: "dotted", borderWidth: "5px"}}
                          accept=".pdf,.jpg,.jpeg,.png,.svg,.gif"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleDirectUpload(val, file);
                            e.target.value = '';
                          }}
                          disabled={isUploadingDocument}
                        />
                        <div className="text-muted d-flex align-items-center">
                          {isUploadingDocument ? (
                            <span className="fw-bold m-auto">Uploading...</span>
                          ) : (
                            <div className="d-flex flex-column flex-sm-row align-items-center gap-3 ms-0 ms-sm-2 text-center text-sm-start">
                              <div className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm border flex-shrink-0" style={{width: '50px', height: '50px'}}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#495057" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                              </div>
                              <div>
                                <div className="mb-1 text-dark" style={{fontSize: '0.95rem'}}><strong className="text-decoration-underline" style={{ cursor: 'pointer' }}>Click to upload</strong> <span className="text-muted">or drag and drop</span></div>
                                <div className="text-muted" style={{fontSize: '0.8rem'}}>PDF, PNG, JPG or JPEG (max. 9MB)</div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="business-kyc__panel">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h6 className="mb-0">Business Directors</h6>
              <button
                type="button"
                className="btn addBank-btn d-flex align-items-center gap-2"
                onClick={openAddDirectorModal}
              >
                <FiPlus />
                <span>Add Director</span>
              </button>
            </div>

            <div className="business-kyc__directors">
              {directors.length === 0 ? (
                <div className="text-center p-4 border rounded bg-light text-muted">
                  No directors added yet. Click <strong>Add Director</strong> to register business owners or directors.
                </div>
              ) : (
                directors.map((director, index) => (
                  <div className="business-kyc__director border p-3 rounded mb-3 bg-white shadow-sm" key={`director-${index}`}>
                    <div className="d-flex justify-content-between align-items-start border-bottom pb-2 mb-3">
                      <div>
                        <h6 className="mb-1 text-primary">
                          {director.firstName || director.lastName
                            ? `${director.firstName} ${director.lastName}`
                            : `Director ${index + 1}`}
                          {director.isPrimaryOwner && (
                            <span className="badge bg-success text-white ms-2" style={{ fontSize: "0.65rem" }}>
                              Primary Owner
                            </span>
                          )}
                          {director.kycStatus && (
                            <span className={`badge ${director.kycStatus == 'approved' ? 'bg-success' : 'bg-warning'} text-white ms-2`} style={{ fontSize: "0.65rem" }}>
                              KYC {director.kycStatus.toUpperCase()}
                            </span>
                          )}
                        </h6>
                        <span className="badge bg-light text-dark border">
                          {director.isOwner ? "Business Owner" : (director.role ? formatLabel(director.role) : "Director")}
                        </span>
                      </div>
                    </div>

                    <div className="row g-2 mb-3" style={{ fontSize: "0.85rem" }}>
                      <div className="col-md-6">
                        <strong>Email:</strong> {director.email || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Phone:</strong> {director.phone || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Gender:</strong> {formatLabel(director.gender) || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Date of Birth:</strong> {director.dateOfBirth || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Job Title:</strong> {director.jobTitle || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Nationality:</strong> {director.nationality || "Pending"}
                      </div>
                      <div className="col-md-6">
                        <strong>Government ID:</strong> {formatLabel(director.kycIdType) || "N/A"}
                      </div>
                      <div className="col-md-6">
                        <strong>Ownership:</strong> {director.percentageOwned || 0}%
                      </div>
                      <div className="col-md-6">
                        <strong>Address:</strong>{" "}
                        {director.dirAddress
                          ? `${director.dirAddress}, ${director.dirCity}, ${director.dirState}, ${director.dirCountryIso2}`
                          : "Not set"}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 border-top pt-2 mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => openEditDirectorModal(index)}
                      >
                        Edit Director
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => openAddressModal(index)}
                      >
                        Update Address
                      </button>
                      {director.id && (
                        <>
                          {!director.isPrimaryOwner && <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={!!directorActionLoading[`primary-${index}`]}
                            onClick={() => handleMarkAsPrimary(index)}
                          >
                            {directorActionLoading[`primary-${index}`]
                              ? "Updating…" : "Mark as Primary"}
                          </button>}
                          {director.kycStatus != 'approved' && <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={!!directorActionLoading[`kyc-${index}`]}
                            onClick={() => handleSendKycInvite(index)}
                          >
                            {directorActionLoading[`kyc-${index}`] ? "Sending…" : "Send KYC Invite"}
                          </button>}
                        </>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm ms-auto"
                        disabled={directors.length === 1 || !!directorActionLoading[`delete-${index}`]}
                        onClick={() => handleDeleteDirector(index)}
                      >
                        {directorActionLoading[`delete-${index}`] ? "Removing…" : "Remove Director"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="business-kyc__panel">
            <h5> Business Details (KYB) Review {kybCanBeSubmitted && 'and Submission'} </h5>
            {submitErrors.length > 0 && (
              <div className="alert alert-warning" role="alert">
                <strong>Complete these tasks before submitting:</strong>
                <ul className="mb-0 mt-2 ps-3">
                  {submitErrors.map((error, index) => (
                    <li key={`submit-error-${index}`}>
                      {error.step ? `Step ${error.step}: ` : ""}
                      {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {['submitted', 'under_review'].includes(userDetails?.business?.kyb_status) && 
              <div className="alert alert-warning" role="alert">
                <strong> KYB Currently In Progress</strong>
                <p className="mb-0 mt-2 ps-3">
                  Your KYB submission is currently under review. The verification process may take up to 48 hours. 
                  We appreciate your patience and will notify you once a decision has been made.
                </p>
              </div>
            }
            <ReviewBlock
              title="Business Details"
              rows={[
                ["Country of Registration", formData.countryOfRegistration],
                ["Business Name", formData.businessName],
                ["Registration Number", formData.registrationNumber],
                ["Registration Date", formData.registrationDate],
                ["Tax ID Number", formData.taxIdNumber],
                [
                  "Registration Type",
                  onboardingOptions.businessRegistrationTypes.find(
                    (t) => t.id === formData.businessRegistrationTypeId,
                  )?.name || formData.businessRegistrationTypeId,
                ],
                [
                  "Industry",
                  onboardingOptions.businessIndustries.find(
                    (i) => i.id === formData.businessIndustryId,
                  )?.name || formData.businessIndustryId,
                ],
                ["Description", formData.description],
                ...(formData.websiteUrl
                  ? [["Website", formData.websiteUrl]]
                  : []),
                [
                  "Address",
                  `${formData.address1}, ${formData.city}, ${formData.stateProvince}, ${formData.countryIso2Code}`,
                ],
              ]}
            />
            <div className="business-kyc__review">
              <h6>Documents</h6>
              {businessDocuments.length === 0 ? (
                <div className="business-kyc__review-row">
                  <strong>No documents uploaded</strong>
                  <span>N/A</span>
                </div>
              ) : (
                businessDocuments.map((document, index) => (
                  <div
                    className="business-kyc__review-row d-flex justify-content-between align-items-center flex-wrap gap-2"
                    key={`review-document-${document.id || document.documentType || index}`}
                  >
                    <div>
                      <strong>{getBusinessDocumentTitle(document)}</strong>
                      <div className="text-muted mt-1" style={{ fontSize: "0.85rem", textAlign: "left" }}>
                        {fileName(document.file)}
                      </div>
                    </div>
                    <div>
                      <span className="badge bg-light text-muted border">
                        {formatLabel(document.verificationStatus || "pending")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="business-kyc__review">
              <h6>Directors</h6>
              {directors.map((director, index) => (
                <div
                  className="business-kyc__review-row d-flex justify-content-between align-items-center flex-wrap gap-2"
                  key={`review-director-${index}`}
                >
                  <div>
                    <strong>
                      {director.firstName} {director.lastName}
                      {director.isPrimaryOwner && (
                        <span className="badge bg-success text-white ms-2" style={{ fontSize: "0.65rem" }}>
                          Primary Owner
                        </span>
                      )}
                      {director.kycStatus && <span className={`badge ${director.kycStatus == 'approved' ? 'bg-success' : 'bg-warning'} text-white ms-2`} style={{ fontSize: "0.65rem" }}>
                          KYC {director.kycStatus.toUpperCase()}
                        </span>}
                    </strong>
                    <div className="text-muted mt-1" style={{ fontSize: "0.85rem", textAlign: "left" }}>
                      {director.role === "owner" ? "Business Owner" : formatLabel(director.role) || "Director"} · {director.jobTitle} ·{" "}
                      {director.percentageOwned}% owned · {director.phone}
                    </div>
                  </div>
                  <div>
                    {director.id ? (
                      director.kycStatus != 'approved' && <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!!directorActionLoading[`kyc-${index}`]}
                        onClick={() => handleSendKycInvite(index)}
                      >
                        {directorActionLoading[`kyc-${index}`] ? "Sending…" : "Send KYC Invite"}
                      </button>
                    ) : (
                      <span className="badge bg-light text-muted border">Pending Save</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {kybCanBeSubmitted && <div className="business-kyc__review mt-3">
              <h6>Declaration</h6>
              <p className="text-muted mb-3">
                Read and accept the following declaration to complete account creation
              </p>
              <p>
                I <strong>{declarantName}</strong> confirm and agree, on behalf of{" "}
                <strong>{declarationBusinessName}</strong>, ("the Business/Merchant"),
                that all information, responses and documents provided to Cardplot
                Technologies Limited are true, accurate, current, and complete to the
                best of my knowledge, including all business, compliance, anti-money
                laundering (AML), and Know Your Business (KYB) information submitted
                during onboarding.
              </p>
              <p>
                That I am duly authorised to act for and bind the Business/Merchant
                and act on behalf of the business in relation to the creation and
                operation of this account on Cardplot; and that no additional approval
                or consent is required from any other person or entity for this
                declaration or onboarding process.
              </p>
              <p>
                That we will promptly notify Cardplot of any material changes to the
                information provided herein.
              </p>
              <p>
                That we authorise Cardplot to conduct all required background checks
                and searches, identity verification, AML screening, sanctions
                screening, compliance reviews, due diligence enquiries and use of our
                information as set out in Cardplot's AML, KYC/KYB and Privacy Policies
                in accordance with applicable laws.
              </p>
              <p>
                That Cardplot may process, store, and use the submitted information
                for regulatory compliance, fraud prevention, risk management,
                transaction processing, and service delivery purposes.
              </p>
              <label className="business-kyc__consent mb-0">
                <input
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={(e) => setDeclarationAccepted(e.target.checked)}
                />
                <span>
                  I acknowledge that I have read and do hereby accept the terms and
                  conditions in Cardplot's Merchant Agreement and the Privacy Policy.
                </span>
              </label>
            </div>}
          </div>
        )}

        <div className="business-kyc__actions">
          <button
            type="button"
            className="btn btn-light border d-flex align-items-center gap-2"
            onClick={goBack}
            disabled={currentStep === 0 || stepLoading}
          >
            <FiChevronLeft />
            <span>Back</span>
          </button>
          <div className="d-flex gap-2">
            {((currentStep === 0 && hasExistingBasicInfo) || (currentStep === 1 && hasExistingAddress)) && (
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={handleSaveStepInfo}
                disabled={saveChangesLoading || stepLoading}
              >
                {saveChangesLoading ? "Saving…" : "Save Changes"}
              </button>
            )}
            {currentStep < businessSteps.length - 1 ? (
              <button
                type="button"
                className="btn addBank-btn d-flex align-items-center gap-2"
                onClick={
                  (currentStep === 0 && !hasExistingBasicInfo) || (currentStep === 1 && !hasExistingAddress)
                    ? handleSaveAndContinue
                    : goNext
                }
                disabled={stepLoading}
              >
                {stepLoading ? (
                  <span>Saving…</span>
                ) : (
                  <>
                    <span>
                      {((currentStep === 0 && !hasExistingBasicInfo) || (currentStep === 1 && !hasExistingAddress))
                        ? "Save and Continue"
                        : "Continue"}
                    </span>
                    <FiChevronRight />
                  </>
                )}
              </button>
            ) : (
              kybCanBeSubmitted ? <button
                type="submit"
                className="btn addBank-btn"
                disabled={stepLoading}
              >
                {stepLoading ? "Submitting…" : "Submit for Review"}
              </button> : <></>
            )}
          </div>
        </div>
      </form>

      {addressModalIndex !== null && (
        <div className="business-kyc__modal-backdrop">
          <div
            className="business-kyc__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="directorAddressTitle"
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 id="directorAddressTitle" className="mb-0">
                Director Address
              </h6>
              <button
                type="button"
                className="btn-close add-director"
                aria-label="Close"
                onClick={() => setAddressModalIndex(null)}
              />
            </div>
            <div className="row">
              <div className="col-12">
                <Field label="Street Address">
                  <input
                    type="text"
                    className="form-control"
                    value={addressDraft.address}
                    onChange={(e) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="City">
                  <input
                    type="text"
                    className="form-control"
                    value={addressDraft.city}
                    onChange={(e) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="State">
                  <input
                    type="text"
                    className="form-control"
                    value={addressDraft.state}
                    onChange={(e) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        state: e.target.value,
                      }))
                    }
                  />
                </Field>
              </div>

              <div className="col-md-6">
                <Field label="Postal Code">
                  <input
                    type="text"
                    className="form-control"
                    value={addressDraft.postalCode}
                    onChange={(e) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        postalCode: e.target.value,
                      }))
                    }
                  />
                </Field>
              </div>

              <div className="col-md-6">
                <Field label="Country">
                  <CountrySelect
                    value={addressDraft.countryIso2Code}
                    onChange={(iso2) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        countryIso2Code: iso2,
                      }))
                    }
                  />
                </Field>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-2">
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setAddressModalIndex(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn addBank-btn"
                onClick={saveAddress}
                disabled={addressLoading}
              >
                {addressLoading ? "Saving…" : "Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}

      {directorPendingDelete && (
        <div className="business-kyc__modal-backdrop">
          <div
            className="business-kyc__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteDirectorModalTitle"
            style={{ maxWidth: "440px", width: "95%" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 id="deleteDirectorModalTitle" className="mb-0">
                Remove Director
              </h6>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setDeleteDirectorIndex(null)}
                disabled={isDeletingDirector}
              />
            </div>
            <p className="mb-0">
              Are you sure you want to remove{" "}
              <strong>
                {directorPendingDeleteName || `Director ${deleteDirectorIndex + 1}`}
              </strong>{" "}
              from the list of directors of your business?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setDeleteDirectorIndex(null)}
                disabled={isDeletingDirector}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={confirmDeleteDirector}
                disabled={isDeletingDirector}
              >
                {isDeletingDirector ? "Removing..." : "Yes, Remove Director"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isDirectorModalOpen && (
        <div className="business-kyc__modal-backdrop">
          <div
            className="business-kyc__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="directorModalTitle"
            style={{ maxWidth: "600px", width: "95%" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 id="directorModalTitle" className="mb-0">
                {editingDirectorIndex !== null ? "Edit Director" : "Add New Director"}
              </h6>
              <button
                type="button"
                className="btn-close add-director"
                aria-label="Close"
                onClick={() => setIsDirectorModalOpen(false)}
              />
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <Field label="First Name">
                  <input
                    type="text"
                    className="form-control"
                    value={directorDraft.firstName || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("firstName", e.target.value)
                    }
                    disabled={directorDraft.kycStatus == 'approved'}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Last Name">
                  <input
                    type="text"
                    className="form-control"
                    value={directorDraft.lastName || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("lastName", e.target.value)
                    }
                    disabled={directorDraft.kycStatus == 'approved'}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Email Address">
                  <input
                    type="email"
                    className="form-control"
                    value={directorDraft.email || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("email", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Phone Number">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="e.g. +234906..."
                    value={directorDraft.phone || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("phone", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Gender">
                  <select
                    className="form-control"
                    value={directorDraft.gender || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("gender", e.target.value)
                    }
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Date of Birth">
                  <input
                    type="date"
                    className="form-control"
                    value={directorDraft.dateOfBirth ? formatDate(directorDraft.dateOfBirth) : ""}
                    onChange={(e) => handleUpdateDirectorDraft("dateOfBirth", e.target.value)}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Role">
                  <select
                    className="form-control"
                    value={directorDraft.role || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleUpdateDirectorDraft("role", val);
                      handleUpdateDirectorDraft("isOwner", val === "owner");
                    }}
                  >
                    <option value="">Select role</option>
                    <option value="director">Director</option>
                    <option value="owner">Business Owner</option>
                  </select>
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Job Title">
                  <select
                    className="form-control"
                    value={directorDraft.jobTitle || ""}
                    onChange={(e) => {
                      handleUpdateDirectorDraft("jobTitle", e.target.value);
                    }}
                  >
                    <option value="">Select Job Title</option>
                    {activeDirectorJobTitles.map((jobTitle, index) => {
                      return (
                        <option key={jobTitle.code || index} value={jobTitle.code}>
                          {jobTitle.label}
                        </option>
                      )
                    })}
                  </select>
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="Nationality">
                  <CountrySelect
                    value={directorDraft.nationality || ""}
                    onChange={(iso2) =>
                      handleUpdateDirectorDraft("nationality", iso2)
                    }
                    disabled={directorDraft.kycStatus == 'approved'}
                  />
                </Field>
              </div>
              <div className="col-md-6">
                <Field label="ID Document For Director KYC">
                  <select
                    className="form-control"
                    value={directorDraft.kycIdType || ""}
                    onChange={(e) => handleUpdateDirectorDraft("kycIdType", e.target.value)}
                    disabled={directorDraft.kycStatus == 'approved'}
                  >
                    <option value="">Select Available ID Document For KYC</option>
                    <option value="nin_slip">NIN</option>
                    <option value="drivers_license">Drivers License</option>
                    <option value="national_id">National ID</option>
                    <option value="passport">International Passport</option>
                  </select>
                </Field>
              </div>
              <div className="col-md-12">
                <Field label="Ownership Percentage (%)">
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    max="100"
                    placeholder="0-100"
                    value={directorDraft.percentageOwned || ""}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("percentageOwned", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="col-12 mt-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isPrimaryOwnerCheck"
                    checked={!!directorDraft.isPrimaryOwner}
                    onChange={(e) =>
                      handleUpdateDirectorDraft("isPrimaryOwner", e.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor="isPrimaryOwnerCheck">
                    Primary Owner
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-light border"
                onClick={() => setIsDirectorModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn addBank-btn"
                onClick={handleSaveDirectorModal}
                disabled={stepLoading}
              >
                {stepLoading ? "Saving..." : editingDirectorIndex !== null ? "Save Changes" : "Add Director"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewBlock = ({ title, rows }) => (
  <div className="business-kyc__review">
    <h6>{title}</h6>
    {rows.map(([label, value]) => (
      <div className="business-kyc__review-row" key={label}>
        <strong>{label}</strong>
        <span>{value || "N/A"}</span>
      </div>
    ))}
  </div>
);

export default BusinessKycStepper;